import { useMemo, useState } from "react";
import {
  KITCHEN_GALLERY,
  WARDROBE_GALLERY,
  RESIDENTIAL_GALLERY,
  GALLERY_EXTRAS,
} from "@/lib/siteData";
import CTASection from "@/components/CTASection";

const CATEGORIES = ["All", "Kitchen", "Wardrobe", "Residential"];

const buildItems = () => {
  // Group by category so "All" view stays: Kitchen → Wardrobe → Residential
  const groups = {
    Kitchen: KITCHEN_GALLERY.map((src) => ({ src, category: "Kitchen" })),
    Wardrobe: WARDROBE_GALLERY.map((src) => ({ src, category: "Wardrobe" })),
    Residential: RESIDENTIAL_GALLERY.map((src) => ({
      src,
      category: "Residential",
    })),
  };
  GALLERY_EXTRAS.forEach((it) => {
    if (groups[it.category]) groups[it.category].push(it);
  });
  return [...groups.Kitchen, ...groups.Wardrobe, ...groups.Residential];
};

const GalleryPage = () => {
  const [filter, setFilter] = useState("All");
  const allItems = useMemo(buildItems, []);
  const items = useMemo(
    () => (filter === "All" ? allItems : allItems.filter((i) => i.category === filter)),
    [filter, allItems]
  );

  return (
    <div data-testid="gallery-page" className="bg-ivory">
      {/* HEADER */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-4xl reveal">
            <div className="overline mb-4">The portfolio</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
              Everything we've<br />
              <span className="italic text-navy">manufactured &amp; installed.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-black/70 leading-relaxed">
              Every Tarush install we can share — kitchens, wardrobes, homes,
              and workspaces — in one place. Filter by category or scroll the
              full run.
            </p>
          </div>

          {/* FILTER TABS */}
          <div
            data-testid="gallery-filters"
            className="mt-10 md:mt-14 flex flex-wrap gap-2 md:gap-3 reveal"
          >
            {CATEGORIES.map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  data-testid={`gallery-filter-${c.toLowerCase()}`}
                  onClick={() => setFilter(c)}
                  className={`px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm tracking-[0.18em] uppercase transition-colors ${
                    active
                      ? "bg-navy text-white"
                      : "bg-white text-black/70 hover:text-navy border border-brandgray"
                  }`}
                >
                  {c}
                  <span className="ml-2 text-[10px] opacity-70">
                    {c === "All"
                      ? allItems.length
                      : allItems.filter((i) => i.category === c).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div
            data-testid="gallery-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {items.map((it, i) => (
              <figure
                key={`${it.src}-${i}`}
                data-testid={`gallery-item-${i}`}
                className="group bg-white p-2 md:p-3 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] ring-1 ring-navy/10 transition-transform duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-cream">
                  <img
                    src={it.src}
                    alt={`${it.category} project ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 text-[10px] tracking-[0.24em] uppercase text-navy">
                    {it.category}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </figure>
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-16 text-black/50">
              No items in this category yet.
            </div>
          )}
        </div>
      </section>

      <CTASection
        eyebrow="Like what you see?"
        title="Let's build yours next."
        body="Share your requirement and our team will respond with a scope, timeline, and indicative pricing."
        primaryLabel="Get a Quotation"
        primaryTo="/contact"
        secondaryLabel="Become a Dealer"
        secondaryTo="/become-a-dealer"
        testid="gallery-cta"
      />
    </div>
  );
};

export default GalleryPage;
